import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  Check,
  CheckCircle2,
  Copy,
  FileSearch,
  FileSignature,
  GraduationCap,
  KeyRound,
} from "lucide-react";

import { useCourierOnboarding } from "features/courier-onboarding/context/CourierOnboardingContext";

const SuccessStep: React.FC = () => {
  const { t } = useTranslation();
  const { data } = useCourierOnboarding();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<number | null>(null);
  const submissionId = data.metadata.submissionId || "";
  const nextSteps = [
    { icon: FileSearch, key: "success.step1" },
    { icon: GraduationCap, key: "success.step2" },
    { icon: FileSignature, key: "success.step3" },
    { icon: BadgeCheck, key: "success.step4" },
  ];

  useEffect(
    () => () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  const copySubmissionId = async () => {
    if (!submissionId) return;
    try {
      await navigator.clipboard.writeText(submissionId);
      setCopied(true);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="relative mx-auto w-full max-w-3xl overflow-hidden text-center"
    >
      <div className="mb-5 flex justify-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-emerald-100 bg-emerald-50 text-emerald-600"
        >
          <CheckCircle2 size={42} strokeWidth={2.5} />
        </motion.div>
      </div>

      <h2 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">
        {t("success.title")}
      </h2>
      <p className="mx-auto mt-3 max-w-lg text-base leading-relaxed text-gray-600">
        {t("success.message", { name: data.personalInfo.fullName })}
      </p>

      <div className="mx-auto mt-8 max-w-2xl">
        <div className="rounded-2xl border-2 border-[#bf0035]/10 bg-[#bf0035]/[0.04] p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#bf0035]">
            {t("success.submissionId")}
          </p>
          <div className="mt-1.5 flex items-center justify-center gap-2">
            <p className="break-all font-mono text-2xl font-extrabold tracking-wide text-[#9b002b] sm:text-3xl">
              {submissionId || "-"}
            </p>
            {submissionId ? (
              <button
                type="button"
                onClick={copySubmissionId}
                aria-label={String(
                  t(copied ? "success.submissionIdCopied" : "success.copySubmissionId"),
                )}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#bf0035]/20 bg-white text-[#bf0035] hover:bg-[#bf0035]/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf0035]"
              >
                {copied ? <Check size={17} /> : <Copy size={17} />}
              </button>
            ) : null}
          </div>
          {copied ? (
            <p role="status" className="mt-2 text-xs font-semibold text-emerald-700">
              {t("success.submissionIdCopied")}
            </p>
          ) : null}
          <div className="mt-3 flex items-start justify-center gap-2 text-start">
            <KeyRound size={16} className="mt-0.5 shrink-0 text-amber-500" />
            <p className="max-w-md text-xs leading-relaxed text-gray-600">
              {t("success.saveSubmissionIdHint")}
            </p>
          </div>
        </div>

        <div className="mt-3 grid gap-3 text-start sm:grid-cols-2">
          {[
            ["success.fullName", data.personalInfo.fullName],
            ["success.email", data.personalInfo.email],
          ].map(([label, value]) => (
            <div
              key={label}
              className="rounded-xl border border-gray-100 bg-gray-50 p-3"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                {t(label)}
              </p>
              <p className="mt-0.5 break-all text-sm font-semibold text-gray-900">
                {value || "-"}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-10 max-w-2xl text-start">
        <h3 className="mb-4 text-center text-sm font-semibold text-gray-900 sm:text-start">
          {t("success.nextStepsTitle")}
        </h3>
        <ol className="flex items-start justify-between gap-1">
          {nextSteps.map(({ icon: Icon, key }, index) => (
            <motion.li
              key={key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.08, duration: 0.35 }}
              className="relative flex flex-1 flex-col items-center text-center"
            >
              {index < nextSteps.length - 1 ? (
                <span
                  aria-hidden
                  className="absolute top-5 h-px bg-gray-200"
                  style={{ insetInlineEnd: "-50%", insetInlineStart: "50%" }}
                />
              ) : null}
              <motion.span
                animate={
                  index === 0
                    ? {
                        boxShadow: [
                          "0 0 0 0 rgba(191, 0, 53, 0.28)",
                          "0 0 0 10px rgba(191, 0, 53, 0)",
                        ],
                      }
                    : undefined
                }
                transition={{ duration: 1.4, repeat: Infinity }}
                className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full ring-4 ring-white ${
                  index === 0
                    ? "bg-[#bf0035]/[0.06] text-[#bf0035]"
                    : "bg-gray-100 text-gray-300"
                }`}
              >
                <Icon size={18} />
              </motion.span>
              <p className="mt-2 text-[10px] font-semibold text-gray-400">
                {String(index + 1).padStart(2, "0")}
              </p>
              <p
                className={`mt-0.5 px-0.5 text-[11px] font-medium leading-tight ${
                  index === 0 ? "text-gray-800" : "text-gray-400"
                }`}
              >
                {t(key)}
              </p>
            </motion.li>
          ))}
        </ol>
      </div>

      <div className="mt-8">
        <p className="mx-auto max-w-md text-sm text-gray-600">
          {t("success.trackStatusHint")}{" "}
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="font-semibold text-[#bf0035] underline underline-offset-2 hover:text-[#99002a]"
          >
            {t("success.trackStatusCta")}
          </button>
        </p>
      </div>
    </motion.section>
  );
};

export default SuccessStep;
